import React, { useState, useEffect } from 'react';
import assistantService from '../../services/assistantService';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';

const { FiUser, FiMic, FiMessageSquare, FiSettings, FiPlus, FiX } = FiIcons;

const AssistantForm = ({ onSuccess, onCancel, editingAssistant }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    voice: 'Lily',
    model: 'gpt-4o-mini',
    first_message: 'Hello! How can I help you today?',
    system_message: 'You are a helpful AI assistant.',
  });
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [voicesLoading, setVoicesLoading] = useState(true);

  useEffect(() => {
    loadVoices();
    if (editingAssistant) {
      console.log('Loading editing assistant data:', editingAssistant);
      setFormData({
        name: editingAssistant.name || '',
        voice: editingAssistant.voice_id || editingAssistant.voice?.voiceId || 'Lily',
        model: editingAssistant.model || 'gpt-4o-mini',
        first_message: editingAssistant.first_message || 'Hello! How can I help you today?',
        system_message: editingAssistant.system_message || editingAssistant.systemMessage || 'You are a helpful AI assistant.',
      });
    }
  }, [editingAssistant]);

  const loadVoices = async () => {
    try {
      setVoicesLoading(true);
      const availableVoices = await assistantService.getAvailableVoices();
      console.log('Loaded voices:', availableVoices);
      setVoices(availableVoices || []);
    } catch (error) {
      console.error('Failed to load voices:', error);
      // Set fallback voices with valid Vapi voice IDs
      setVoices([
        { voiceId: 'Lily', name: 'Lily (Female, American)', provider: 'vapi' },
        { voiceId: 'Elliot', name: 'Elliot (Male, American)', provider: 'vapi' },
        { voiceId: 'Kylie', name: 'Kylie (Female, American)', provider: 'vapi' },
        { voiceId: 'Cole', name: 'Cole (Male, American)', provider: 'vapi' }
      ]);
    } finally {
      setVoicesLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user?.id) {
      toast.error('You must be logged in to create assistants');
      return;
    }

    if (!formData.name.trim()) {
      toast.error('Please enter a name for your assistant');
      return;
    }

    setLoading(true);

    try {
      // Find the selected voice object
      const selectedVoice = voices.find(v => v.voiceId === formData.voice);
      console.log('Selected voice:', selectedVoice);
      console.log('Form voice value:', formData.voice);

      const assistantData = {
        name: formData.name.trim(),
        voice: selectedVoice ? {
          voiceId: selectedVoice.voiceId,
          name: selectedVoice.name,
          provider: selectedVoice.provider
        } : {
          voiceId: formData.voice,
          name: formData.voice,
          provider: 'vapi'
        },
        model: formData.model,
        firstMessage: formData.first_message,
        systemMessage: formData.system_message,
      };

      console.log('Submitting assistant data:', assistantData);

      if (editingAssistant) {
        await assistantService.updateAssistant(editingAssistant.id, assistantData, user.id);
        toast.success('Assistant updated successfully!');
      } else {
        await assistantService.createAssistant(assistantData, user.id);
        toast.success('Assistant created successfully!');
      }
      onSuccess();
    } catch (error) {
      toast.error(`Failed to save assistant: ${error.message}`);
      console.error('Error saving assistant:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Show login prompt if user is not authenticated
  if (!user) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center"
          >
            <SafeIcon icon={FiUser} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Please log in</h3>
            <p className="text-gray-600 mb-6">You need to be logged in to create assistants</p>
            <button
              onClick={onCancel}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={(e) => e.target === e.currentTarget && onCancel()}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingAssistant ? 'Edit Assistant' : 'Create New Assistant'}
              </h2>
              <button
                onClick={onCancel}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiUser} className="inline w-4 h-4 mr-2" />
                  Assistant Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Enter assistant name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiMic} className="inline w-4 h-4 mr-2" />
                  Voice
                </label>
                {voicesLoading ? (
                  <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Loading voices...
                  </div>
                ) : (
                  <>
                    <select
                      name="voice"
                      value={formData.voice}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    >
                      {voices.length > 0 ? (
                        voices.map((voice) => (
                          <option key={voice.voiceId} value={voice.voiceId}>
                            {voice.name}
                          </option>
                        ))
                      ) : (
                        <option value="Lily">Lily (Default)</option>
                      )}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Selected: {formData.voice} {voices.find(v => v.voiceId === formData.voice) ? `(${voices.find(v => v.voiceId === formData.voice).provider})` : ''}
                    </p>
                  </>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiSettings} className="inline w-4 h-4 mr-2" />
                  Model
                </label>
                <select
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                >
                  <option value="gpt-4o-mini">GPT-4o Mini (Recommended)</option>
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                  <option value="gpt-4">GPT-4</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiMessageSquare} className="inline w-4 h-4 mr-2" />
                  First Message
                </label>
                <textarea
                  name="first_message"
                  value={formData.first_message}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                  placeholder="What should the assistant say first?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  System Message
                </label>
                <textarea
                  name="system_message"
                  value={formData.system_message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                  placeholder="Define the assistant's personality and behavior..."
                />
              </div>

              <div className="flex gap-4 pt-4">
                <motion.button
                  type="button"
                  onClick={onCancel}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="submit"
                  disabled={loading || voicesLoading}
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </div>
                  ) : (
                    editingAssistant ? 'Update Assistant' : 'Create Assistant'
                  )}
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AssistantForm;